import { Button, Card, CardContent, CardFooter, CardHeader, CardTitle, Input } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useInterruptionSettings, useUpdateGeneralSettings } from '@/entities/settings';
import type { GeneralSettingsUpdate } from '@/entities/settings';
import { NON_NEGATIVE_INTEGER_REGEX } from '@/shared/lib';

type Props = {
  /** Изменение общих настроек доступно только с правом администрирования раздела */
  canEdit: boolean;
};

/** Пороги в форме — строки: поле ввода отдаёт введённое значение как есть, в число оно переводится при сохранении */
type InterruptionFormValues = {
  /** Минимальная длительность перебивания в миллисекундах */
  interruptionMinDurationMs: string;
  /** Максимальная длительность перебивания в миллисекундах */
  interruptionMaxDurationMs: string;
  /** Минимальная длительность паузы в миллисекундах */
  pauseMinDurationMs: string;
};

/** Пороги карточки: имена полей формы совпадают с именами настроек в теле запроса сохранения */
const DURATION_FIELDS = ['interruptionMinDurationMs', 'interruptionMaxDurationMs', 'pauseMinDurationMs'] as const;

/**
 * Карточка раздела «Общее» с порогами распознавания перебиваний и пауз.
 * Свои данные и их сохранение держит сама: страница только размещает карточку.
 */
export const InterruptionSettingsCard: FC<Props> = ({ canEdit }) => {
  const { t } = useTranslation(['common', 'generalSettings']);
  const { interruptionMinDurationMs, interruptionMaxDurationMs, pauseMinDurationMs } = useInterruptionSettings();
  const { mutate: updateGeneralSettings, isPending } = useUpdateGeneralSettings();

  // Действующие значения — источник истины формы: после сохранения она показывает сохранённое
  const values = useMemo<InterruptionFormValues>(
    () => ({
      interruptionMinDurationMs: String(interruptionMinDurationMs),
      interruptionMaxDurationMs: String(interruptionMaxDurationMs),
      pauseMinDurationMs: String(pauseMinDurationMs),
    }),
    [interruptionMinDurationMs, interruptionMaxDurationMs, pauseMinDurationMs],
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { dirtyFields },
  } = useForm<InterruptionFormValues>({ values, mode: 'onBlur' });

  const durationRules = useMemo(
    () => ({
      validate: (value: string) =>
        NON_NEGATIVE_INTEGER_REGEX.test(value.trim()) || t('generalSettings:interruption.errors.nonNegativeInteger'),
    }),
    [t],
  );

  // Отправляем только изменённые пороги: непереданные бэк оставляет как есть,
  // и правка соседнего порога другим администратором не затирается
  const submit = handleSubmit((formValues) => {
    const changedSettings: GeneralSettingsUpdate = {};
    for (const field of DURATION_FIELDS) {
      if (dirtyFields[field]) {
        changedSettings[field] = Number(formValues[field]);
      }
    }
    if (Object.keys(changedSettings).length === 0) {
      return;
    }
    updateGeneralSettings(changedSettings);
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('generalSettings:interruptionSection')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-start">
          <Input
            type="number"
            label={t('generalSettings:interruption.minInterruptionDuration')}
            control={control}
            name="interruptionMinDurationMs"
            disabled={!canEdit || isPending}
            rules={durationRules}
          />
          <Input
            type="number"
            label={t('generalSettings:interruption.maxInterruptionDuration')}
            control={control}
            name="interruptionMaxDurationMs"
            disabled={!canEdit || isPending}
            rules={durationRules}
          />
          <Input
            type="number"
            label={t('generalSettings:interruption.minPauseDuration')}
            control={control}
            name="pauseMinDurationMs"
            disabled={!canEdit || isPending}
            rules={durationRules}
          />
        </div>
        <p className="text-sm text-muted-foreground">{t('generalSettings:interruption.description')}</p>
      </CardContent>
      {canEdit && (
        <CardFooter className="gap-3 w-full justify-end">
          <Button variant="secondary" onClick={() => reset()}>
            {t('common:cancel')}
          </Button>
          <Button onClick={submit} isLoading={isPending}>
            {t('common:save')}
          </Button>
        </CardFooter>
      )}
    </Card>
  );
};
